import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { SystemDesign } from "../types/SystemDesign";
import { Helmet } from "react-helmet";
import { systemDesignApi } from "../api/systemDesignApi";
import {
  ArrowLeft,
  Save,
  X,
  Upload,
  Trash2,
} from "lucide-react";

const companies = [
  "Google", "Amazon", "Facebook", "Apple", "Microsoft",
  "Netflix", "Uber", "Airbnb", "Twitter", "LinkedIn",
  "Stripe", "Dropbox", "Spotify", "Slack", "Zoom",
];

const difficulties = ["Easy", "Medium", "Hard"] as const;

const SystemDesignFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [company, setCompany] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [difficulty, setDifficulty] = useState<"Easy" | "Medium" | "Hard">("Medium");
  const [images, setImages] = useState<File[]>([]);
  const [existingImageUrls, setExistingImageUrls] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (id) {
      fetchSystemDesign(parseInt(id));
    }
  }, [id]);

  const fetchSystemDesign = async (designId: number) => {
    try {
      const design = await systemDesignApi.getById(designId);
      setCompany(design.company);
      setTitle(design.title);
      setDescription(design.description);
      setDifficulty(design.difficulty);
      setExistingImageUrls(design.images || []);
    } catch (error) {
      console.error("Error fetching system design:", error);
    }
  };

  const handleImageUpload = (files: FileList | null) => {
    if (files && files.length > 0) {
      const remaining = 3 - images.length - existingImageUrls.length;
      const newImages = Array.from(files).slice(0, remaining);
      setImages([...images, ...newImages]);
    }
  };

  const removeExistingImage = (index: number) => {
    setExistingImageUrls(existingImageUrls.filter((_, i) => i !== index));
  };

  const removeImage = (index: number) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const uploadToPinata = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await axios.post(
      "https://api.pinata.cloud/pinning/pinFileToIPFS",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          pinata_api_key: process.env.REACT_APP_PINATA_API_KEY,
          pinata_secret_api_key: process.env.REACT_APP_PINATA_SECRET_API_KEY,
        },
      }
    );
    return `https://gateway.pinata.cloud/ipfs/${res.data.IpfsHash}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const newImageUrls = await Promise.all(images.map(uploadToPinata));
      const allImageUrls = [...existingImageUrls, ...newImageUrls];

      const designData = {
        company,
        title,
        description,
        difficulty,
        images: allImageUrls,
      };

      if (id) {
        await systemDesignApi.update(parseInt(id), {
          id: parseInt(id),
          ...designData,
        });
      } else {
        await systemDesignApi.create(designData);
      }

      navigate("/system-design");
    } catch (error) {
      console.error("Error saving system design:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const getDifficultyColor = (level: string) => {
    switch (level) {
      case "Easy":
        return "bg-emerald-500/15 text-emerald-400 border-emerald-500/30";
      case "Medium":
        return "bg-amber-500/15 text-amber-400 border-amber-500/30";
      case "Hard":
        return "bg-red-500/15 text-red-400 border-red-500/30";
      default:
        return "bg-gray-500/15 text-gray-400 border-gray-500/30";
    }
  };

  const getDifficultyActiveColor = (level: string) => {
    switch (level) {
      case "Easy":
        return "bg-emerald-500/25 text-emerald-300 border-emerald-500/50";
      case "Medium":
        return "bg-amber-500/25 text-amber-300 border-amber-500/50";
      case "Hard":
        return "bg-red-500/25 text-red-300 border-red-500/50";
      default:
        return "bg-gray-500/25 text-gray-300 border-gray-500/50";
    }
  };

  return (
    <>
      <Helmet>
        <title>{id ? "Edit" : "Add New"} System Design - AlgoBuddy</title>
        <meta
          name="description"
          content={`${id ? "Edit existing" : "Create a new"} system design entry.`}
        />
      </Helmet>

      <main className="min-h-screen bg-gray-950">
        {/* Top bar */}
        <div className="sticky top-0 z-20 bg-gray-950/80 backdrop-blur-lg border-b border-gray-800/50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
            <button
              onClick={() => navigate(id ? `/system-design/${id}` : "/system-design")}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {id ? "Back" : "System Design"}
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSaving || !title.trim() || !company}
              className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium bg-white text-gray-950 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-gray-950/30 border-t-gray-950 rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-3.5 h-3.5" />
                  Publish
                </>
              )}
            </button>
          </div>
        </div>

        {/* Editor */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-24">
          <form onSubmit={handleSubmit}>
            {/* Title */}
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Untitled"
              className="w-full text-4xl sm:text-5xl font-bold text-white bg-transparent border-none outline-none placeholder-gray-700 mb-6 tracking-tight"
              required
            />

            {/* Meta row */}
            <div className="flex flex-wrap items-center gap-3 mb-8 pb-8 border-b border-gray-800/60">
              {/* Company pills */}
              <div className="flex flex-wrap items-center gap-1.5">
                {companies.slice(0, 8).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCompany(c)}
                    className={`px-3 py-1 text-xs font-medium rounded-full border transition-all ${
                      company === c
                        ? "bg-blue-500/20 text-blue-300 border-blue-500/40"
                        : "bg-gray-800/50 text-gray-500 border-gray-800 hover:text-gray-300 hover:border-gray-700"
                    }`}
                  >
                    {c}
                  </button>
                ))}
                {/* More companies dropdown */}
                {company && !companies.slice(0, 8).includes(company) && (
                  <span className="px-3 py-1 text-xs font-medium rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/40">
                    {company}
                  </span>
                )}
              </div>

              {/* Divider */}
              <div className="w-px h-5 bg-gray-800" />

              {/* Difficulty pills */}
              <div className="flex items-center gap-1.5">
                {difficulties.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDifficulty(d)}
                    className={`px-3 py-1 text-xs font-medium rounded-full border transition-all ${
                      difficulty === d
                        ? getDifficultyActiveColor(d)
                        : "bg-gray-800/50 text-gray-500 border-gray-800 hover:text-gray-300 hover:border-gray-700"
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>

            {/* Company dropdown for all options */}
            <div className="mb-8">
              <label className="text-xs text-gray-600 uppercase tracking-wider font-medium">
                Company
              </label>
              <select
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="mt-2 w-full sm:w-auto px-4 py-2 bg-gray-900 border border-gray-800 rounded-lg text-sm text-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-700 transition-colors appearance-none cursor-pointer"
                required
              >
                <option value="">Select company</option>
                {companies.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div className="mb-10">
              <label className="text-xs text-gray-600 uppercase tracking-wider font-medium">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the system design problem, requirements, constraints, and your approach..."
                className="mt-3 w-full min-h-[400px] text-lg text-gray-300 bg-transparent border-none outline-none placeholder-gray-700 leading-[1.85] font-light resize-none"
                required
              />
            </div>

            {/* Images */}
            <div>
              <label className="text-xs text-gray-600 uppercase tracking-wider font-medium">
                Diagrams
                {existingImageUrls.length + images.length > 0 && (
                  <span className="ml-2 text-gray-700">
                    {existingImageUrls.length + images.length}/3
                  </span>
                )}
              </label>

              {/* Existing images */}
              {existingImageUrls.length > 0 && (
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {existingImageUrls.map((url, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={url}
                        alt={`Diagram ${index + 1}`}
                        className="w-full h-32 object-cover rounded-xl border border-gray-800"
                      />
                      <button
                        type="button"
                        onClick={() => removeExistingImage(index)}
                        className="absolute top-2 right-2 p-1.5 bg-gray-950/80 text-gray-400 hover:text-red-400 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* New images preview */}
              {images.length > 0 && (
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {images.map((file, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-32 object-cover rounded-xl border border-gray-800"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-2 right-2 p-1.5 bg-gray-950/80 text-gray-400 hover:text-red-400 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload area */}
              {existingImageUrls.length + images.length < 3 && (
                <label className="mt-3 flex items-center justify-center gap-2 w-full h-24 border border-dashed border-gray-800 rounded-xl cursor-pointer hover:border-gray-700 hover:bg-gray-900/50 transition-all">
                  <Upload className="w-4 h-4 text-gray-600" />
                  <span className="text-sm text-gray-600">Add diagram</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleImageUpload(e.target.files)}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            {/* Bottom actions */}
            <div className="mt-16 pt-8 border-t border-gray-800/60 flex items-center justify-between">
              <button
                type="button"
                onClick={() => navigate(id ? `/system-design/${id}` : "/system-design")}
                className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving || !title.trim() || !company}
                className="flex items-center gap-2 px-5 py-2.5 bg-white text-gray-950 font-medium rounded-xl hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-gray-950/30 border-t-gray-950 rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {id ? "Update" : "Publish"} Question
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
    </>
  );
};

export default SystemDesignFormPage;
